<template>
  <div v-if="state" class="flex flex-col gap-3">
    <div>
      <div class="text-base font-semibold">
        {{ t(effectiveBeneficiaryDocumentRequirementCopy[requirement.id].titleKey) }}
        <span class="text-error text-lg leading-none">*</span>
      </div>
      <p class="text-sm text-medium-emphasis m-0">
        {{ t(effectiveBeneficiaryDocumentRequirementCopy[requirement.id].hintKey) }}
      </p>
    </div>

    <KycDocumentItem
      v-for="document in requirementDocuments"
      :key="document.documentId"
      :document="document"
      :label-key="documentTypeLabelKey(document.documentType)"
      :user-id="userId"
    />

    <div class="mt-2 flex flex-col gap-1">
      <div class="flex flex-wrap items-center gap-3">
        <v-select
          v-if="requirement.anyOf.length > 1"
          v-model="state.documentTypeId"
          class="w-full flex-none sm:w-56"
          :items="documentTypeItems"
          :label="t('treezorOnboarding.documents.documentType')"
          density="compact"
          hide-details="auto"
          :error-messages="
            missingDocumentRequirement && !state.documentTypeId ? t('errors.required') : []
          "
          :disabled="state.uploading || !canEditKyc"
        />
        <v-file-input
          :model-value="state.file"
          class="min-w-60 grow"
          density="compact"
          variant="outlined"
          prepend-inner-icon="mdi-paperclip"
          :prepend-icon="''"
          hide-details="auto"
          accept=".jpg,.jpeg,.png,.svg,.tif,.tiff,.pdf,image/jpeg,image/png,image/svg+xml,image/tiff,application/pdf"
          :rules="[maxFileSize(50)]"
          :error-messages="missingDocumentRequirement && !state.file ? t('errors.required') : []"
          :loading="state.uploading"
          :disabled="state.uploading || !canEditKyc || !state.documentTypeId"
          :label="t('treezorOnboarding.documents.chooseFile')"
          @update:model-value="onFileUpdate"
        />
      </div>
      <p class="text-xs text-medium-emphasis m-0">{{ t("treezorOnboarding.documents.hint") }}</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { useToast } from "@/store";
import type {
  PreReviewDocument,
  TreezorKycEffectiveBeneficiaryDocumentRequirement,
} from "@aircap/common/treezor";
import { captureException } from "@sentry/vue";
import { computed } from "vue";
import KycDocumentItem from "./KycDocumentItem.vue";
import {
  documentTypeLabelKey,
  effectiveBeneficiaryDocumentRequirementCopy,
  useTreezorOnboarding,
  type FileInputValue,
} from "./useTreezorOnboarding";

const props = defineProps<{
  userId?: number;
  requirement: TreezorKycEffectiveBeneficiaryDocumentRequirement;
  documents: PreReviewDocument[];
  showMissing: boolean;
}>();

const { t } = useI18n();
const { maxFileSize } = useRules();
const { showSuccessToast, showErrorToast } = useToast();
const {
  canEditKyc,
  effectiveBeneficiaries,
  effectiveBeneficiaryDocumentState,
  kycReviewReadiness,
  loadEffectiveBeneficiaries,
  uploadTreezorDocumentFile,
} = useTreezorOnboarding();

const state = computed(() =>
  props.userId === undefined
    ? undefined
    : effectiveBeneficiaryDocumentState.value[props.userId]?.[props.requirement.id],
);

const requirementDocuments = computed(() =>
  props.documents.filter((document) =>
    props.requirement.anyOf.some((documentTypeId) => documentTypeId === document.documentType),
  ),
);

const missingDocumentRequirement = computed(
  () =>
    props.showMissing &&
    !!kycReviewReadiness.value.incompleteEffectiveBeneficiaries
      .find((person) => person.userId === props.userId)
      ?.missingDocumentRequirements.some(
        (missingRequirement) => missingRequirement.id === props.requirement.id,
      ),
);

const documentTypeItems = computed(() =>
  props.requirement.anyOf.map((documentTypeId) => ({
    title: t(`treezorOnboarding.documents.${documentTypeLabelKey(documentTypeId)}`),
    value: documentTypeId,
  })),
);

const onFileUpdate = (value: FileInputValue) => {
  if (!state.value || state.value.uploading) return;
  const file = Array.isArray(value) ? (value[0] ?? null) : value;
  state.value.file = file;
  if (!file || maxFileSize(50)(file) !== true) return;
  void onUpload();
};

const onUpload = async () => {
  const { userId } = props;
  const uploadState = state.value;
  if (!canEditKyc.value || userId === undefined || !uploadState?.file) return;
  if (!uploadState.documentTypeId) return;
  if (maxFileSize(50)(uploadState.file) !== true) return;

  uploadState.uploading = true;
  try {
    const documentId = await uploadTreezorDocumentFile({
      file: uploadState.file,
      documentTypeId: uploadState.documentTypeId,
      userId,
    });
    if (!documentId) return;
    let found = false;
    for (let attempt = 0; attempt < 8; attempt++) {
      await loadEffectiveBeneficiaries({ silent: true });
      if (
        effectiveBeneficiaries.value
          .find((effectiveBeneficiary) => effectiveBeneficiary.user.userId === userId)
          ?.documents.some((document) => document.documentId === documentId)
      ) {
        found = true;
        break;
      }
      await new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });
    }
    if (!found) throw new Error("Uploaded document was not found after upload");
    showSuccessToast(t("treezorOnboarding.documents.uploadSuccess"));
  } catch (cause) {
    const error = new Error("Error uploading Treezor effective beneficiary document", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
  } finally {
    uploadState.file = null;
    uploadState.uploading = false;
  }
};
</script>
